import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import CancelDialogIcon from "@entrilia/shared/icons/CancelDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { api } from "../../../../api/client";
import useImpersonationBlockContext from "../../../../contexts/useImpersonationBlockContext";

interface Props {
  open: boolean;
  onClose: () => void;
  investorId: string;
  workflowId: string;
  referenceCode: string | undefined;
  onChangeRequestCanceled: () => void;
  title?: string;
  heading?: string;
  description?: string;
  confirmLabel?: string;
  successMessage?: string;
}

const cancelChangeRequest = withErrorHandling(api.changeRequests.cancelChangeRequest);

const ConfirmChangeRequestCancellationDialog = ({
  open,
  onClose,
  investorId,
  workflowId,
  referenceCode,
  onChangeRequestCanceled,
  title = "Cancel Request",
  heading,
  description = "Your requested changes will be discarded.",
  confirmLabel = "Cancel Request",
  successMessage = "The request was canceled",
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { isBlockedUnderImpersonation, showImpersonationBlockedDialog } = useImpersonationBlockContext();
  const [loading, setLoading] = useState(false);

  const handleCancelChangeRequest = async () => {
    setLoading(true);
    const [, error] = await cancelChangeRequest(investorId, { workflowId });
    setLoading(false);

    if (error) {
      if (isBlockedUnderImpersonation(error)) {
        showImpersonationBlockedDialog();
        return;
      }
      logError(error, "[ConfirmChangeRequestCancellationDialog] cancelChangeRequest");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(successMessage);
    onChangeRequestCanceled?.();
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <CancelDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={0.5} sx={{ alignItems: "center" }}>
            <Typography variant="h6">
              {heading ?? (referenceCode ? `Cancel update request #${referenceCode}?` : "Cancel update request?")}
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              {description}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelText="Close">
        <Button variant="outlined" color="error" loading={loading} onClick={handleCancelChangeRequest}>
          {confirmLabel}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default ConfirmChangeRequestCancellationDialog;
